/**
 * Client-side session tracking for the story player.
 *
 * All functions are pure — they return new objects instead of mutating state.
 * The session merges locally recorded visits/responses with server-persisted
 * data so the player can resume across page reloads or devices.
 * @module session
 */

/**
 * Create a fresh session, optionally hydrated from a server-persisted session.
 * @param {Object|null} serverSession - Server session data to merge, or null for a blank session
 * @returns {Object} Session object with visits, responses, and completion tracking
 */
export function createSession(serverSession = null) {
  const base = {
    id: null,
    startedAt: Date.now(),
    completedAt: null,
    visits: [],
    responses: {},
    serverResponses: [],
    completedThreads: [],
    completedBeats: [],
  }

  if (serverSession) {
    return mergeServerSession(base, serverSession)
  }
  return base
}

/**
 * Merge server-persisted state into an existing local session.
 * Server responses are keyed by beat_id so duplicate entries overwrite cleanly.
 * @param {Object} session - Current local session
 * @param {Object} serverSession - Server session data (snake_case fields from Rails)
 * @returns {Object} Merged session with server IDs, timestamps, and responses
 */
export function mergeServerSession(session, serverSession) {
  // Build responses map from server data
  const responses = { ...session.responses }
  for (const entry of serverSession.responses || []) {
    responses[entry.beat_id] = entry.response_data
  }

  return {
    ...session,
    id: serverSession.id,
    startedAt: serverSession.started_at ? new Date(serverSession.started_at).getTime() : session.startedAt,
    completedAt: serverSession.completed_at ? new Date(serverSession.completed_at).getTime() : null,
    responses,
    serverResponses: serverSession.responses || [],
    completedThreads: serverSession.completed_threads || [],
    completedBeats: serverSession.completed_beats || [],
  }
}

/**
 * Record that the player visited a beat (appends, never deduplicates).
 * @param {Object} session - Current session
 * @param {Object} beat - Beat object (must have .id)
 * @returns {Object} Updated session with the new visit appended
 */
export function recordBeatVisit(session, beat) {
  return {
    ...session,
    visits: [...session.visits, { beatId: beat.id, timestamp: Date.now() }],
  }
}

/**
 * Store the player's response for a beat (overwrites any previous response).
 * @param {Object} session - Current session
 * @param {string|number} beatId - Beat ID to associate the response with
 * @param {*} response - Response data (choice object, text input, etc.)
 * @returns {Object} Updated session with the response recorded
 */
export function recordBeatResponse(session, beatId, response) {
  return {
    ...session,
    responses: { ...session.responses, [beatId]: response },
  }
}

/**
 * Mark the session as completed with a timestamp.
 * @param {Object} session - Current session
 * @returns {Object} Updated session with completedAt set to now
 */
export function completeSession(session) {
  return { ...session, completedAt: Date.now() }
}

/**
 * Get all beat IDs the player has visited, combining local visits with
 * server-tracked completions. Returns a deduplicated array.
 * @param {Object} session - Current session
 * @returns {Array<string|number>} Unique beat IDs visited across local and server state
 */
export function getVisitedBeatIds(session) {
  // Combine local visits with server completed beats
  const localIds = session.visits.map((v) => v.beatId)
  const serverIds = session.completedBeats || []
  return [...new Set([...localIds, ...serverIds])]
}

/**
 * Retrieve the stored response for a specific beat.
 * @param {Object} session - Current session
 * @param {string|number} beatId - Beat ID to look up
 * @returns {*|null} The response data, or null if no response exists
 */
export function getBeatResponse(session, beatId) {
  return session.responses[beatId] ?? null
}
